import { useCallback, useState } from 'react';
import { apiService } from 'utils/api';
import { handleApiError } from 'utils/functions';
import { ENUM_ITEM_TYPE, ENUM_ORDER_PROVIDER } from 'utils/enum';
import { MealType, CategoryMealType } from 'models/meal.model';
import { CoKitchenType } from 'models/cokitchen.model';
import { MealCategoryType } from 'models/meal-category.model';
import { RestaurantType } from 'models/brand.model';
import { UpdateOrderAddonCategoryType } from 'models/addon-category.model';

export const useRestaurantMeals = () => {
  const [step, setStep] = useState(1);
  const [brands, setBrands] = useState<RestaurantType[]>([]);
  const [restaurant, setRestaurant] = useState<RestaurantType | null>(null);
  const [mealLoading, setMealLoading] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState<CategoryMealType | null>(null);

  const getBrands = useCallback(async (activeCokitchen: CoKitchenType) => {
    if (!activeCokitchen) return;
    setMealLoading(true);
    try {
      const res = await apiService.get('v2/brand_management/brands', {
        params: {
          cokitchen_id: activeCokitchen.id,
          live: true,
          item_type: ENUM_ITEM_TYPE.FOOD,
          brand_type: ENUM_ORDER_PROVIDER.foodcourt
        },
        baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
      });

      setBrands(res?.data?.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      setMealLoading(false);
    }
  }, []);

  const getMealCategories = useCallback(async (id: string) => {
    const res = await apiService.get(`marketing/brand/meal-categories/${id}`, {
      baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
    });
    return Promise.all(
      res.data.data.map(async (category: MealCategoryType) => {
        return {
          id: category.id,
          name: category.name,
          meals: await getMeals(category.id)
        };
      })
    );
  }, []);

  const getMeals = useCallback(async (id: string) => {
    const res = await apiService.get(`marketing/meal-categories/meals/${id}`, {
      baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
    });
    return res.data.map((m: MealType) => ({
      id: m.id,
      name: m.name,
      description: m.description,
      amount: Number(m.amount),
      image: m?.images?.[0],
      active: m?.active,
      available_no: m?.available_no
    })) as CategoryMealType[];
  }, []);

  const loadRestaurant = useCallback(
    async (id?: string) => {
      if (!id) return;
      if (id === restaurant?.id) {
        goToStep(2);
        return;
      }
      try {
        setMealLoading(true);
        const res = await apiService.get(`brand_management/brands/${id}`, {
          baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
        });
        const restaurantData: RestaurantType = res?.data?.data;

        if (!restaurantData) return;

        restaurantData.meal_categories = await getMealCategories(id);
        setRestaurant(restaurantData);
        setStep(2);
      } catch (error) {
        handleApiError(error);
      } finally {
        setMealLoading(false);
      }
    },
    [restaurant?.id]
  );

  const goToStep = (number: number) => {
    setStep(number);
  };

  const loadMeal = async (id?: string) => {
    if (!id) return;
    if (id === selectedMeal?.id) {
      goToStep(3);
      return;
    }
    try {
      setMealLoading(true);
      const res: any = await apiService.get(`marketing/get-meal-by-id/${id}`, {
        baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
      });
      const meal = res.data.data;

      setSelectedMeal({
        ...meal,
        id: meal.id,
        name: meal.name,
        description: meal.description,
        amount: Number(meal.amount),
        image: meal?.images?.[0],
        restaurant: { ...meal.brand, logo: '' },
        addon_categories: await getAddonCategories(meal.id),
        active: meal?.active,
        available_no: meal?.available_no
      });
      setStep(3);
    } catch (error) {
      handleApiError(error);
    } finally {
      setMealLoading(false);
    }
  };

  const getAddonCategories = async (meal_id: string) => {
    const data = { meal_id, by_category: true };
    const res = await apiService.post('internal/meal-addons', {
      data,
      isInternal: true,
      baseUrl: process.env.REACT_APP_BRAND_MGT_API_URL
    });

    return res.data.data.addons.map((category: any) => ({
      id: category.id,
      name: category.name || category.base_addon_category.name,
      summary: category.summary,
      selection_type: category.meal_category_selection_type.name,
      max_selection: Number(category.selection_no) || Infinity,
      addons: category.addons.map((addon: any) => ({
        ...addon,
        quantity: 0,
        default: addon.id === category.meal_default_id,
        amount: Number(addon.amount)
      }))
    })) as UpdateOrderAddonCategoryType[];
  };

  return {
    step,
    goToStep,
    brands,
    restaurant,
    mealLoading,
    getBrands,
    loadRestaurant,
    loadMeal,
    selectedMeal
  };
};
